import React, { useEffect } from "react";
import "../../css/components/reusable/ReusableForm.css";

const ReusableForm = ({
    isOpen,
    onClose,
    title,
    fields = [],
    initialValues = {},
    onSubmit,
    submitLabel = "Create",
    children,
}) => {
    // Close on ESC key
    useEffect(() => {
        const handleEsc = (e) => { if (e.key === "Escape") onClose(); };
        document.addEventListener("keydown", handleEsc);
        return () => document.removeEventListener("keydown", handleEsc);
    }, [onClose]);

    if (!isOpen) return null;

    const handleSubmit = (e) => {
        e.preventDefault();
        const formData = new FormData(e.target);
        const data = Object.fromEntries(formData.entries());

        // Preserve existing behavior:
        // empty fields are ignored.
        for (const [key, value] of formData.entries()) {
            if (value !== "") { data[key] = value; }
        }
        onSubmit(data);
    };

    const renderField = (field) => {
        const {
            name,
            label,
            type = "text",
            required = false,
            placeholder = "",
            readOnly = false,
            options = [],
        } = field;

        const value = initialValues?.[name] ?? "";

        const fieldClassName = readOnly ? "readonly-input" : "";

        if (type === "select") {
            return (
                <select
                    name={name}
                    defaultValue={value}
                    required={required}
                    disabled={readOnly}
                    className={fieldClassName}
                >
                    <option value="">
                        {placeholder || `Select ${label}`}
                    </option>

                    {options.map((option, index) => {
                        const optionValue =
                            typeof option === "object"
                                ? option.value
                                : option;

                        const optionLabel =
                            typeof option === "object"
                                ? option.label
                                : option;

                        return (
                            <option
                                key={`${name}-${optionValue}-${index}`}
                                value={optionValue}
                            >
                                {optionLabel}
                            </option>
                        );
                    })}
                </select>
            );
        }

        if (type === "textarea") {
            return (
                <textarea
                    name={name}
                    defaultValue={value}
                    placeholder={placeholder}
                    required={required}
                    readOnly={readOnly}
                    className={fieldClassName}
                />
            );
        }

        // Existing/default input behavior
        return (
            <input
                type={type}
                name={name}
                defaultValue={value}
                placeholder={placeholder}
                required={required}
                readOnly={readOnly}
                className={fieldClassName}
            />
        );
    };

    return (
        <div className="modal-overlay" onClick={onClose}>
            <div
                className="modal-container"
                onClick={(e) => e.stopPropagation()}
            >
                <h2>{title}</h2>

                <p className="form-note">
                    Fields marked with * are required
                </p>

                <form onSubmit={handleSubmit} className="modal-form">
                    {fields.map((field, index) => (
                        <div
                            key={field.name || index}
                            className="form-group"
                        >
                            <label htmlFor={field.name}>
                                {field.label}

                                {field.required && (
                                    <span className="required">*</span>
                                )}
                            </label>

                            {renderField(field)}
                        </div>
                    ))}

                    {/* CUSTOM CONTENT (LINE ITEMS ETC.) */}
                    {children}

                    <div className="modal-actions">
                        <button
                            type="submit"
                            className="create-btn"
                        >
                            {submitLabel}
                        </button>

                        <button
                            type="button"
                            className="cancel-btn"
                            onClick={onClose}
                        >
                            Cancel
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default ReusableForm;
